package com.siriusxm.transcripts

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent

/**
 * A custom timeline scrubber component for navigating through time-based content.
 *
 * @param currentTime The current time position in milliseconds
 * @param totalDuration The total duration in milliseconds
 * @param onTimeChange Callback when the time position changes
 */
@Composable
fun TimelineScrubber(
    currentTime: Long,
    totalDuration: Long,
    onTimeChange: (Long) -> Unit
) {
    var isDragging by remember { mutableStateOf(false) }
    val scrubberContainerId = remember { "timeline-scrubber-${currentTime.hashCode()}" }

    // Calculate position as percentage
    val position = if (totalDuration > 0) {
        ((currentTime.toDouble() / totalDuration.toDouble()) * 100).coerceIn(0.0, 100.0)
    } else {
        0.0
    }

    LaunchedEffect(isDragging, scrubberContainerId) {
        if (isDragging) {
            // Add global mousemove handler while dragging
            val mouseMoveHandler: (org.w3c.dom.events.Event) -> Unit = { event ->
                val mouseEvent = event as? MouseEvent
                val scrubberElement = document.getElementById(scrubberContainerId) as? HTMLElement
                if (mouseEvent != null && scrubberElement != null) {
                    val rect = scrubberElement.getBoundingClientRect()
                    val x = mouseEvent.clientX - rect.left
                    val percent = (x / rect.width).coerceIn(0.0, 1.0)
                    val newTime = (percent * totalDuration.toDouble()).toLong()
                    onTimeChange(newTime)
                }
            }

            val mouseUpHandler: (org.w3c.dom.events.Event) -> Unit = {
                isDragging = false
            }

            document.addEventListener("mousemove", mouseMoveHandler)
            document.addEventListener("mouseup", mouseUpHandler)

            // Cleanup when effect is disposed or isDragging changes
            kotlinx.coroutines.coroutineScope {
                try {
                    kotlinx.coroutines.awaitCancellation()
                } finally {
                    document.removeEventListener("mousemove", mouseMoveHandler)
                    document.removeEventListener("mouseup", mouseUpHandler)
                }
            }
        }
    }

    Div({
        id(scrubberContainerId)
        style {
            width(100.percent)
            height(24.px)
            position(Position.Relative)
            property("cursor", "pointer")
            property("user-select", "none")
        }
        onClick { event ->
            if (!isDragging) {
                val target = event.currentTarget as? HTMLElement
                if (target != null) {
                    val rect = target.getBoundingClientRect()
                    val x = event.clientX - rect.left
                    val percent = (x / rect.width).coerceIn(0.0, 1.0)
                    val newTime = (percent * totalDuration.toDouble()).toLong()
                    onTimeChange(newTime)
                }
            }
        }
        onMouseDown { event ->
            isDragging = true
            event.preventDefault()
            // Immediately update position on mousedown
            val target = event.currentTarget as? HTMLElement
            if (target != null) {
                val rect = target.getBoundingClientRect()
                val x = event.clientX - rect.left
                val percent = (x / rect.width).coerceIn(0.0, 1.0)
                val newTime = (percent * totalDuration.toDouble()).toLong()
                onTimeChange(newTime)
            }
        }
    }) {
        // Track background
        Div({
            style {
                width(100.percent)
                height(4.px)
                position(Position.Absolute)
                top(10.px)
                backgroundColor(SXMUI.borderDividerDefault.value())
                borderRadius(2.px)
            }
        })

        // Progress fill
        Div({
            style {
                width(position.percent)
                height(4.px)
                position(Position.Absolute)
                top(10.px)
                backgroundColor(SXMUI.brandColor.value())
                borderRadius(2.px)
                property("pointer-events", "none")
            }
        })

        // Scrubber handle
        Div({
            style {
                width(16.px)
                height(16.px)
                position(Position.Absolute)
                top(4.px)
                property("left", "calc(${position}% - 8px)")
                backgroundColor(SXMUI.brandColor.value())
                borderRadius(50.percent)
                border(2.px, LineStyle.Solid, Color.white)
                property("box-shadow", "0 2px 4px rgba(0,0,0,0.2)")
                property("pointer-events", "none")
                property("transition", if (isDragging) "none" else "left 0.1s ease-out")
            }
        })
    }
}
